'use client';

/**
 * TextInput - テキスト入力コンポーネント
 *
 * 2 モードを variant で切り替え（HTML の type と衝突しないよう variant を使用）:
 * - form（デフォルト）: react-hook-form と連携。control / name / label 必須。
 * - standalone: フォーム外で単体利用。value / onChange で制御。見た目は form と同じ。
 */

import {
  ChangeEvent,
  ComponentPropsWithoutRef,
  RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';

import classNames from 'classnames';
import {
  Control,
  FieldPath,
  FieldValues,
  useController,
} from 'react-hook-form';

import Dropdown, { useDropdown } from '~/app/_components/PortalDropdown';

import styles from './style.module.css';

/** variant='form' 用。react-hook-form の control と name が必須。HTML の type（text/password 等）も渡せる */
export type TextInputFormProps<T extends FieldValues> =
  ComponentPropsWithoutRef<'input'> & {
    variant?: 'form';
    name: FieldPath<T>;
    control: Control<T>;
    label: string;
    /** 入力欄右に表示する単位（例: "分"） */
    unit?: string;
    /** 入力欄下の補足文 */
    note?: string;
    /** フォーカス時にドロップダウンで表示する候補一覧 */
    suggestions?: Suggestion[];
  };

/** variant='standalone' 用。value / onChange で制御。フォーム外で単体利用するとき用 */
export type TextInputStandaloneProps = Omit<
  ComponentPropsWithoutRef<'input'>,
  'value' | 'onChange'
> & {
  variant: 'standalone';
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  /** フローティングラベルとして表示するテキスト（form と同じ見た目用） */
  label?: string;
};

export type TextInputProps<T extends FieldValues> =
  | TextInputFormProps<T>
  | TextInputStandaloneProps;

/** スタンドアロン用の入力。form と同じ DOM 構造・スタイルで描画 */
const TextInputStandalone: React.FC<TextInputStandaloneProps> = ({
  variant: _variant,
  value,
  onChange,
  label,
  type,
  ...props
}) => (
  <div className={classNames(styles.textInput)}>
    <div className={styles.unitContainer}>
      <div className={styles.container}>
        {/* placeholder=' ' は CSS の :placeholder-shown でフローティングラベル制御に利用 */}
        <input
          className={styles.input}
          placeholder=' '
          value={value}
          onChange={onChange}
          {...props}
          type={type ?? 'text'}
        />
        {label ? <label className={styles.label}>{label}</label> : null}
      </div>
    </div>
  </div>
);

type Suggestion = { value: string | number; label: string };

/** サジェストのドロップダウン。Dropdown の開閉と入力欄の幅を使うため、Dropdown の内側で描画する */
const SuggestionsBody = ({
  inputRef,
  suggestions,
  onSelect,
}: {
  inputRef: RefObject<HTMLInputElement | null>;
  suggestions: Suggestion[];
  onSelect: (value: Suggestion['value']) => void;
}) => {
  const { setIsOpen, triggerSize } = useDropdown();

  // Tab などで入力欄からフォーカスが外れたら閉じる
  // （候補のクリックは pointerdown/mousedown で blur を防いでいるので閉じない）
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return undefined;

    const close = () => setIsOpen(false);
    input.addEventListener('blur', close);
    return () => input.removeEventListener('blur', close);
  }, [inputRef, setIsOpen]);

  // closeOnClickItem だとスクロールバーのクリックでも閉じるため、候補を選んだときだけ閉じる
  return (
    <Dropdown.Body width={triggerSize.width}>
      {/* スクロールバーの操作でも入力欄のフォーカスを外さない */}
      <div
        className={styles.suggestions}
        onMouseDown={(e) => e.preventDefault()}
      >
        {suggestions.map((suggestion) => (
          <button
            key={suggestion.value}
            className={styles.item}
            onPointerDown={(e) => {
              e.preventDefault();
              inputRef.current?.focus();
            }}
            onClick={() => {
              inputRef.current?.focus();
              onSelect(suggestion.value);
              setIsOpen(false);
            }}
          >
            {suggestion.label}
          </button>
        ))}
      </div>
    </Dropdown.Body>
  );
};

/** フォーム用の入力。useController で react-hook-form と連携し、suggestions は Dropdown で表示 */
const TextInputWithControl = <T extends FieldValues>({
  variant: _variant,
  name,
  control,
  label,
  note,
  unit,
  suggestions,
  type,
  ...props
}: TextInputFormProps<T>) => {
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
  });

  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // サジェスト選択後も input にフォーカスを戻すため、isFocused で明示的に focus/blur を制御
  useEffect(() => {
    if (isFocused) {
      inputRef.current?.focus();
    } else {
      inputRef.current?.blur();
    }
  }, [isFocused]);

  return (
    <Dropdown tabIndex={1}>
      <div className={styles.textInput}>
        <div
          className={styles.unitContainer}
          onClick={() => {
            setIsFocused(true);
          }}
        >
          <Dropdown.Trigger as='div'>
            <div className={styles.container}>
              <input
                className={styles.input}
                placeholder=' '
                {...field}
                {...props}
                ref={inputRef}
                // 入力欄のクリックは Dropdown.Trigger が伝播を止めるため、外側の onClick では
                // isFocused が立たずサジェストが出ない。フォーカス時にも立てる
                onFocus={(e) => {
                  setIsFocused(true);
                  props.onFocus?.(e);
                }}
                autoComplete={suggestions?.length ? 'off' : 'on'}
                type={type ?? 'text'}
              />
              <label className={styles.label} htmlFor={name}>
                {label}
              </label>
            </div>
          </Dropdown.Trigger>
          {unit && <span className={styles.unit}>{unit}</span>}
        </div>
        {note && <span className={styles.note}>{note}</span>}
        {error && <span className={styles.error}>{error.message}</span>}

        {/* サジェストがあるときだけ、フォーカス中はドロップダウンを表示 */}
        {suggestions && isFocused && (
          <SuggestionsBody
            inputRef={inputRef}
            suggestions={suggestions}
            onSelect={field.onChange}
          />
        )}
      </div>
    </Dropdown>
  );
};

/** variant に応じて form 用 or standalone 用の実装を切り替え。省略時は form */
export const TextInput = <T extends FieldValues>(props: TextInputProps<T>) => {
  if (props.variant === 'standalone') {
    return <TextInputStandalone {...(props as TextInputStandaloneProps)} />;
  }
  return <TextInputWithControl {...(props as TextInputFormProps<T>)} />;
};
